import { useState, useEffect, useCallback } from "react";
import usePermissions from "./usePermissions";
import { getPitchScoresApi } from "../api/pitch.api";
import { useListState } from "../libs/listState";

const PAGE_SIZE = 20;

export default function usePitchScores({
  baseFilters = {},
  failSilently = false,
  // Name under which the page, sorting and filters are remembered, to be put back when the user
  // returns from a detail. Leave undefined for a list that should not remember anything.
  persistKey,
} = {}) {
  const { state, update, restoreScrollOnce, markLeaving } = useListState(
    persistKey,
    {
      options: {
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: "score",
        sortDirection: "desc",
      },
      filters: { ...baseFilters },
    },
  );
  const { options, filters } = state;
  const setOptions = useCallback(
    (value) => update({ options: value }),
    [update],
  );
  const setFilters = useCallback(
    (value) =>
      update((prev) => ({
        filters: typeof value === "function" ? value(prev.filters) : value,
      })),
    [update],
  );

  const [pitchScoresData, setPitchScoresData] = useState(null);
  const [pitchScoresLoading, setPitchScoresLoading] = useState(false);

  const permissions = usePermissions();
  console.log("usePitchScores render");

  // eslint-disable-next-line no-shadow
  const getPitchScores = useCallback(async () => {
    console.log("getPitchScores");
    const { page, pageSize, sortBy, sortDirection } = options;
    setPitchScoresLoading(true);
    try {
      const sortFieldMap = {
        company: "company",
        category: "pitchCategory",
        endDate: "latestEndDate",
        scope: "pitchScope",
        score: "pitchScore",
      };
      const data = await getPitchScoresApi({
        data: {
          page,
          pageSize,
          sortBy: sortBy ? sortFieldMap[sortBy] || sortBy : sortBy,
          sortDirection,
          ...filters,
        },
      });
      setPitchScoresData(data);
      setPitchScoresLoading(false);
    } catch (e) {
      console.error(e);
      if (!failSilently) window.setError(e);
    }
  }, [filters, options]);

  useEffect(() => {
    console.log("usePitchScores effect");
    if (permissions.pitchScores) {
      getPitchScores();
    } else {
      console.log(
        "not getting pitch scores cause lacking permission",
        permissions,
      );
    }
  }, [getPitchScores, permissions]);

  // Once the rows are on screen, put the scroll back where the user left it
  useEffect(() => {
    if (!pitchScoresLoading && pitchScoresData) restoreScrollOnce();
  }, [pitchScoresLoading, pitchScoresData, restoreScrollOnce]);

  return {
    markLeaving,
    data: pitchScoresData,
    loading: pitchScoresLoading,
    options,
    setOptions,
    filters,
    setFilters,
    reload: getPitchScores,
  };
}
